import type { ScannerEvent } from "./types/scanner.ts";

/**
 * Install the global ScannerBridge that the Android shell calls into.
 *
 * In production: the Android shell calls window.ScannerBridge.onScan(raw) for every scan.
 * In dev: keyboard input fires the same event so the PWA can be exercised without the shell.
 *
 * Call this once at app startup, before any consumer subscribes.
 */
export function installScannerBridge(): (() => void) | undefined {
  const handlers = new Set<(event: ScannerEvent) => void>();

  window.ScannerBridge = {
    onScan(raw: string, symbology?: string) {
      const event: ScannerEvent = {
        rawBarcode: raw,
        symbology: normaliseSymbology(symbology),
        capturedAt: new Date().toISOString(),
      };
      for (const handler of handlers) handler(event);
    },
    subscribe(handler) {
      handlers.add(handler);
      return () => handlers.delete(handler);
    },
  };

  // Keyboard fallback: capture rapid char input followed by Enter, which is what a
  // scanner emits in HID/keystroke mode. Used in dev, and whenever we're NOT hosted by
  // the Android shell (i.e. a plain browser) — the shell pushes scans via DataWedge
  // Intents instead, so we must not double-handle there.
  const inAndroidShell = typeof window.AndroidScannerBridge !== "undefined";
  if (import.meta.env.DEV || !inAndroidShell) {
    return installKeyboardSimulator();
  }
}

function normaliseSymbology(value: string | undefined): ScannerEvent["symbology"] {
  switch (value?.toLowerCase()) {
    case "datamatrix":
    case "code128":
    case "code39":
    case "ean13":
    case "qr":
      return value.toLowerCase() as ScannerEvent["symbology"];
    default:
      return "other";
  }
}

function installKeyboardSimulator(): () => void {
  let buffer = "";
  let lastKeyAt = 0;
  let flushTimer: ReturnType<typeof setTimeout> | undefined;
  // Gap before a keystroke starts a fresh scan. Generous because DataWedge keystroke
  // injection can be slower than a true HID scanner.
  const RESET_AFTER_MS = 200;
  // Quiet period after the last character — treat as end-of-scan even if no suffix key
  // (Enter/CR) was configured in DataWedge.
  const FLUSH_AFTER_MS = 150;

  const flush = () => {
    if (flushTimer) {
      clearTimeout(flushTimer);
      flushTimer = undefined;
    }
    if (buffer.length > 0) {
      // DataWedge keystroke mode renders a GS1 symbol's FNC1 group separators as "]" (and
      // may prefix an AIM symbology id such as "]d2"). Strip a leading AIM id and the "]"
      // separators to recover the flat element string the rules expect. Keystroke/browser
      // path only — the Android shell's Intent path delivers the raw data string untouched.
      const raw = buffer.replace(/^\][A-Za-z][A-Za-z0-9]/, "").replace(/\]/g, "");
      buffer = "";
      window.ScannerBridge.onScan(raw);
    }
  };

  const handler = (e: KeyboardEvent) => {
    const target = e.target as HTMLElement | null;
    // Don't intercept while typing in inputs
    if (target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA")) return;

    const now = performance.now();
    if (now - lastKeyAt > RESET_AFTER_MS) buffer = "";
    lastKeyAt = now;

    // Explicit end-of-scan suffix: Enter, or a CR/LF some keystroke modes emit.
    if (e.key === "Enter" || e.key === "\r" || e.key === "\n") {
      flush();
      return;
    }
    if (e.key.length === 1) {
      buffer += e.key;
      // Fallback: flush after a quiet gap if no suffix key arrives.
      if (flushTimer) clearTimeout(flushTimer);
      flushTimer = setTimeout(flush, FLUSH_AFTER_MS);
    }
  };

  window.addEventListener("keydown", handler);
  return () => {
    if (flushTimer) clearTimeout(flushTimer);
    window.removeEventListener("keydown", handler);
  };
}
